import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { UserPublic } from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { setToken } from '@/lib/auth';
import { useRefreshCurrentUser } from '@/hooks/useCurrentUser';
import { ButtonSpinner, FormField, inputClasses, primaryButtonClasses } from './FormField';
import { GoogleSignInButton } from './GoogleSignInButton';

interface Props {
  googleClientId: string | null;
  // When the login page was arrived at from `/accept_group_invite/:token`,
  // `next` is the path to resume after a successful login. Password login and
  // Google sign-in both honor it. `inviteToken` is carried through to the
  // Google endpoint so a first-time Google user is auto-joined server-side.
  next?: string | null;
  inviteToken?: string | null;
}

export function LoginForm({ googleClientId, next, inviteToken }: Props) {
  const navigate = useNavigate();
  const refreshMe = useRefreshCurrentUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerificationForEmail, setNeedsVerificationForEmail] = useState<string | null>(null);
  const [resendPending, setResendPending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setNeedsVerificationForEmail(null);
    setResendDone(false);
    setPending(true);
    try {
      const { token } = await api.post<{ user: UserPublic; token: string }>(
        '/api/auth/login',
        { email, password },
      );
      setToken(token);
      await refreshMe();
      navigate(next ?? '/');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.code === 'EMAIL_NOT_VERIFIED') {
          setNeedsVerificationForEmail(email);
        }
      } else {
        setError('Login failed');
      }
    } finally {
      setPending(false);
    }
  }

  async function onResend() {
    if (!needsVerificationForEmail) return;
    setResendPending(true);
    try {
      await api.post<{ ok: true }>('/api/auth/resend-verification', {
        email: needsVerificationForEmail,
      });
    } catch {
      /* non-enumerating endpoint always returns 200 — ignore */
    } finally {
      setResendPending(false);
      setResendDone(true);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <FormField label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            disabled={pending}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setNeedsVerificationForEmail(null);
              setResendDone(false);
            }}
            className={inputClasses}
          />
        </FormField>
        <FormField label="Password" htmlFor="password">
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            disabled={pending}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setNeedsVerificationForEmail(null);
              setResendDone(false);
            }}
            className={inputClasses}
          />
        </FormField>
        <Link
          to="/forgot-password"
          className="self-start text-sm text-[color:var(--color-fg-muted)] underline underline-offset-2"
        >
          Forgot password?
        </Link>
        {error ? (
          <p className="text-sm text-[color:var(--color-danger)]">{error}</p>
        ) : null}
        {needsVerificationForEmail && !resendDone ? (
          <button
            type="button"
            onClick={onResend}
            disabled={resendPending}
            aria-busy={resendPending}
            className="self-start text-sm text-[color:var(--color-fg)] underline underline-offset-2 disabled:opacity-60"
          >
            {resendPending ? 'Resending…' : 'Resend verification email'}
          </button>
        ) : null}
        {resendDone ? (
          <p className="text-xs text-[color:var(--color-fg-muted)]">
            If an account with that email needs verification, we sent a new link.
          </p>
        ) : null}
        <button
          type="submit"
          disabled={pending}
          aria-busy={pending}
          className={primaryButtonClasses}
        >
          {pending ? (
            <>
              <ButtonSpinner />
              <span>Signing in…</span>
            </>
          ) : (
            'Log in'
          )}
        </button>
      </form>

      <div className="flex items-center gap-3 text-xs text-[color:var(--color-fg-muted)]">
        <span className="h-px flex-1 bg-[color:var(--color-border)]" />
        or
        <span className="h-px flex-1 bg-[color:var(--color-border)]" />
      </div>

      <GoogleSignInButton
        clientId={googleClientId}
        next={next ?? null}
        inviteToken={inviteToken ?? null}
      />
    </div>
  );
}
